import React, { FunctionComponent, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { Box, Card, DialogActions, DialogContent, Toolbar, useTheme } from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { strings } from "../../../localization/LocalizedStrings";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { TextField } from "../../TextField";
import { AlertDialog } from "../../AlertDialog";
import { toast } from "react-toastify";
import { FieldTitle } from "../../Text/FieldTitle";
import { ContainedButton } from "../../ContainedButton";
import { ToolbarButton } from "../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../hooks/isMobile";
import { FreeTextListItem } from "../../FreeTextListItem";
import { ModifyFreeTextAttributeDialog } from "../../attributes/ModifyFreeTextAttributeDialog";
import { IShelving } from "../../../hooks/shelvings";
import { IShelf } from "../../../hooks/shelves";
import { useAddShelf } from "../../../hooks/shelves/useAddShelf";
import { useModifyShelf } from "../../../hooks/shelves/useModifyShelf";
import { Firebase } from "../../../services/Firebase";
import { PiperDialog } from "../../PiperDialog";
import { IconType } from "../../../icons";

export const ModifyShelfDialog: FunctionComponent<IModifyShelfDialogProps> = ({
  open,
  setOpen,
  shelf,
  shelving,
  callback,
  onDelete
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [dialogTitle, setDialogTitle] = useState(strings.shelf_new_title);
  const { addShelf } = useAddShelf();
  const { modifyShelf } = useModifyShelf();
  const [isModifyDescriptionDialogOpen, setIsModifyDescriptionDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmCloseAlertOpen, setIsConfirmCloseAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const tryToClose = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    if (isLoading) return;
    if (formik.dirty) {
      setIsConfirmCloseAlertOpen(true);
      return;
    }
    handleClose();
  };
  const handleClose = () => {
    setOpen(false);
  };
  const validationSchema = Yup.object({
    name: Yup.string().required(strings.field_missing)
  });
  const formik = useFormik({
    initialValues: {
      name: shelf?.name ?? "",
      description: shelf?.description ?? ""
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async ({ name, description }) => {
      setIsLoading(true);
      let statusCode, responseBody;
      if (shelf) {
        const { status, body } = await modifyShelf({
          uuid: shelf.uuid,
          name: name,
          description: description
        });
        statusCode = status;
        responseBody = body;
      } else {
        const { status, body } = await addShelf({
          name: name,
          description: description,
          shelvingId: shelving.uuid
        });
        statusCode = status;
        responseBody = body;
      }
      if (!StatusCodesHelper.isSuccessful(statusCode)) {
        Firebase.shared.logFirebaseEvent("shelf_action");
        setAlertMessage(responseBody.message);
        setAlertIsOpen(true);
      } else {
        if (callback != undefined) {
          callback(responseBody);
        }
        handleClose();
        toast.success(strings.generic_update_success);
      }
      setIsLoading(false);
    },
    validationSchema: validationSchema
  });
  useEffect(() => {
    if (!open) return;
    setDialogTitle(shelf ? strings.shelf_title : strings.shelf_new_title);
    formik.resetForm();
  }, [open]);
  return (
    <FormikProvider value={formik}>
      <PiperDialog fullWidth maxWidth={"xs"} open={open} onClose={tryToClose}>
        <Toolbar>
          <h3>{dialogTitle}</h3>
          <Box sx={{ flexGrow: 1 }} />
          {shelf && (
            <ToolbarButton
              disabled={isLoading}
              onClick={onDelete}
              tooltip={strings.delete}
              iconType={IconType.DeleteForever}
              color={theme.palette.colors?.remove}
            />
          )}
          <ToolbarButton
            loading={isLoading}
            onClick={tryToClose}
            tooltip={strings.close}
            iconType={IconType.Close}
          />
        </Toolbar>
        <DialogContent sx={{ paddingTop: 0, paddingBottom: 0 }}>
          <FieldTitle>{strings.name_title}</FieldTitle>
          <div className={styles.textField}>
            <TextField
              id="name"
              name="name"
              size="small"
              autoFocus={!isMobile}
              placeholder={strings.shelf_name_placeholder}
              value={formik.values.name}
              onChange={formik.handleChange}
            />
          </div>
          <FieldTitle>{strings.collection_description}</FieldTitle>
          <Card className={styles.descriptionCard}>
            <FreeTextListItem
              hideTitle
              detail={formik.values.description ?? "--"}
              maxLines={4}
              onClick={() => setIsModifyDescriptionDialogOpen(true)}
            />
          </Card>
        </DialogContent>
        <DialogActions>
          <ContainedButton type={"submit"} onClick={formik.handleSubmit}>
            {strings.alert_confirm}
          </ContainedButton>
        </DialogActions>
      </PiperDialog>
      <ModifyFreeTextAttributeDialog
        title={strings.collection_description}
        description={strings.edit_description_description}
        placeholder={strings.collection_description}
        value={formik.values.description}
        open={isModifyDescriptionDialogOpen}
        onClose={() => setIsModifyDescriptionDialogOpen(false)}
        onChange={newValue => formik.setFieldValue("description", newValue)}
        onClearValue={() => formik.setFieldValue("description", null)}
      />
      <AlertDialog
        message={strings.alert_cancel_confirmation}
        open={isConfirmCloseAlertOpen}
        setOpen={setIsConfirmCloseAlertOpen}
        isConfirm={true}
        onConfirm={handleClose}
        confirmTitle={strings.yes}
        cancelTitle={strings.no}
      />
      <AlertDialog message={alertMessage} open={alertIsOpen} setOpen={setAlertIsOpen} />
    </FormikProvider>
  );
};

export interface IModifyShelfDialogProps {
  open: boolean;
  setOpen: (state: boolean) => void;
  shelf?: IShelf;
  shelving: IShelving;
  callback?: (newShelf: IShelf) => void;
  onDelete?: () => void;
}
